import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Spin, Select, InputNumber, Divider, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchCounties,
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
} from "../../../features/fetch/fetchSlice";
import CardLoading from "../../../components/CardLoading";
import { schReclStatusData } from "../../../data";

const GrassBandAdd = () => {
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";
  const urlTypography = "typography/fetch_typographys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { counties, subCounties, locations, subLocations } = useSelector(
    (state) => state.fetch
  );
  const {
    loading,
    activeObjGrassBand,
    dataPathway,
    dataConstituency,
    dataWard,
    dataTypography,
  } = useSelector((state) => ({
    activeObjGrassBand: state.obj.activeObj["grassBand"],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
    dataTypography: state.fetch.data[urlTypography] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/semicircular-bands-for-grass");
  }

  const [grassCountyId, setGrassCountyId] = useState(
    activeObjGrassBand?.grassCountyId ?? ""
  );
  const [grassConstId, setGrassConstId] = useState(
    activeObjGrassBand?.grassConstId ?? ""
  );
  const [grassSubCountyId, setGrassSubCountyId] = useState(
    activeObjGrassBand?.grassSubCountyId ?? ""
  );
  const [grassLocationId, setGrassLocationId] = useState(
    activeObjGrassBand?.grassLocationId ?? ""
  );
  const [grassSubLocationId, setGrassSubLocationId] = useState(
    activeObjGrassBand?.grassSubLocationId ?? ""
  );
  const [grassWardId, setGrassWardId] = useState(
    activeObjGrassBand?.grassWardId ?? ""
  );

  const handleGrassCountyChange = async (value) => {
    setGrassCountyId(value);
    setGrassConstId("");
    setGrassSubCountyId("");
    setGrassLocationId("");
    setGrassSubLocationId("");
    setGrassWardId("");

    form.setFieldsValue({
      grassCountyId: value,
      grassConstId: "",
      grassSubCountyId: "",
      grassLocationId: "",
      grassSubLocationId: "",
      grassWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(fetchData({ url: urlConstituency, constCountyId: value }));
  };

  const handleGrassConstituencyChange = async (value) => {
    setGrassConstId(value);
    setGrassWardId("");

    form.setFieldsValue({
      grassConstId: value,
      grassWardId: "",
    });

    await dispatch(fetchData({ url: urlWard, wdConstId: value }));
  };

  const handleGrassSubCountyChange = async (value) => {
    setGrassSubCountyId(value);
    setGrassLocationId("");
    setGrassSubLocationId("");

    form.setFieldsValue({
      grassSubCountyId: value,
      grassLocationId: "",
      grassSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleGrassLocationChange = async (value) => {
    setGrassLocationId(value);
    setGrassSubLocationId("");

    form.setFieldsValue({
      grassLocationId: value,
      grassSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleGrassSubLocationChange = (value) => {
    setGrassSubLocationId(value);
    form.setFieldsValue({ grassSubLocationId: value });
  };

  const handleGrassWardChange = (value) => {
    setGrassWardId(value);
    form.setFieldsValue({ grassWardId: value });
  };

  const onFinish = async (data) => {
    data.grassId = activeObjGrassBand?.grassId
      ? activeObjGrassBand?.grassId
      : null;

    data.grassPathwayId = Array.isArray(data?.grassPathwayId)
      ? data?.grassPathwayId.join(",")
      : data?.grassPathwayId;

    const res = await dispatch(
      save({
        ...data,
        url: "grass_band/save_grassband",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjGrassBand,
    });
    if (activeObjGrassBand?.grassPathwayId) {
      form.setFieldsValue({
        grassPathwayId: activeObjGrassBand?.grassPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjGrassBand, form]);

  const initialFormValues = {
    ...activeObjGrassBand,
  };

  useEffect(() => {
    dispatch(fetchCounties());
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlTypography,
        limit: null,
        start: 0,
      })
    );
  }, []);

  useEffect(() => {
    if (activeObjGrassBand?.grassId) {
      dispatch(
        fetchSubCounties({ subCountyId: activeObjGrassBand?.grassCountyId })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjGrassBand?.grassCountyId,
        })
      );
      dispatch(
        fetchData({ url: urlWard, wdConstId: activeObjGrassBand?.grassConstId })
      );
      dispatch(
        fetchLocation({ locSubId: activeObjGrassBand?.grassSubCountyId })
      );
      dispatch(
        fetchSubLocation({ slocLocId: activeObjGrassBand?.grassLocationId })
      );
    }
  }, [activeObjGrassBand, form]);

  if (loading) return <CardLoading />;

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          {
            title: "Grass Band List",
            path: "/rob/semicircular-bands-for-grass",
          },
          { title: "Grass Bands Add Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="w-full">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <div className="custom-title !bg-white !px-0">
              Semi-Circular Bunds For Grass Details
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="grassName"
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add project name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="grassStatus"
                className=""
                label={"Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    schReclStatusData?.length > 0 &&
                    schReclStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="grassCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleGrassCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {grassCountyId && (
                <>
                  <Form.Item
                    name="grassConstId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleGrassConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="grassSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleGrassSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {grassSubCountyId && (
                <Form.Item
                  name="grassLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleGrassLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {grassLocationId && (
                <Form.Item
                  name="grassSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleGrassSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {grassConstId && (
                <Form.Item
                  name="grassWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleGrassWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="grassLat"
                label={"Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="grassLong"
                label={"Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="grassSize"
                label={"Size (Acres)"}
                rules={[
                  {
                    required: true,
                    message: "Please add size",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="grassGroundSlopeId"
                className=""
                label={"Slope of The Ground"}
                rules={[
                  {
                    required: true,
                    message: "Please add slope of the ground",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataTypography?.length > 0 &&
                    dataTypography?.map((item) => ({
                      value: item?.typoId,
                      label: item?.typoTypography,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="grassDiameterBand"
                label={"Diameter of the bund (Meters)"}
                rules={[
                  {
                    required: true,
                    message: "Please add Diameter of the bund (Meters)",
                  },
                ]}
              >
                <InputNumber
                  suffix={"M"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="grassMaxBandHeight"
                label={"Maximum Height of The Bund"}
                rules={[
                  {
                    required: true,
                    message: "Please add Maximum height of the bund",
                  },
                ]}
              >
                <InputNumber
                  suffix={"M"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="grassMaxWaterDepth"
                label={"Maximum Depth of Water "}
                rules={[
                  {
                    required: true,
                    message: "Please add maximum depth of water ",
                  },
                ]}
              >
                <InputNumber
                  suffix={"M"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="grassCatchmentArea"
                label={"Catchment Area"}
                rules={[
                  {
                    required: true,
                    message: "Please add catchment area",
                  },
                ]}
              >
                <InputNumber
                  suffix={"M"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                label={"Pathways"}
                name="grassPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjGrassBand?.grassId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default GrassBandAdd;
